import React, { useEffect, useState, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import CardContainer from '../../components/card/card-container.jsx';
import Api from '../../api/global.service';
import env from '../../environment.js';
import GraficaSensorial from './components/grafica-sensorial.jsx';
import GraficaDefectos from './components/grafica-defectos.jsx';
import {
  calcRendimientoBruto,
  calcManchaPorcentaje,
  calcDefectosScore,
  calcPuntajeSensorial,
  calcTotalDefectosRedondeado,
} from './components/calidad-calculos.js';
import LinkDownload from '../../components/link/link-download.jsx';
import { getJSON } from '../../utils/localStorageJSON.js';

const Separator = () => (
  <div className="row mt-3">
    <div className="col-lg-12">
      <hr />
    </div>
  </div>
);

const RowItem = ({ label, value, size = 'md', stacked = false }) =>
  stacked ? (
    <div className="mb-2">
      <div style={{ color: '#333', fontWeight: 600, fontSize: size === 'sm' ? '0.8rem' : '0.9rem' }}>{label}</div>
      <div style={{ fontWeight: 500, fontSize: size === 'sm' ? '0.8rem' : '0.9rem' }}>{value ?? '-'}</div>
    </div>
  ) : (
    <div className="row mb-2">
      <div className="col-lg-4" style={{ color: '#333', fontWeight: 600, fontSize: size === 'sm' ? '0.9rem' : '1rem' }}>
        {label}
      </div>
      <div className="col-lg-8" style={{ fontWeight: 500, fontSize: size === 'sm' ? '0.9rem' : '1rem' }}>
        {value ?? '-'}
      </div>
    </div>
  );

const ListTable = ({ headers = [], rows = [], className = '', equalCols = false, style = {}, compact = false }) => {
  const colWidth = equalCols && headers.length > 0 ? `${(100 / headers.length).toFixed(4)}%` : undefined;
  const denseCellStyle = compact ? { padding: '4px 6px', lineHeight: 1.2 } : {};
  return (
    <div className={`table-responsive ${className}`}>
      <table
        className="table table-sm"
        style={{ ...(equalCols ? { tableLayout: 'fixed', width: '100%' } : {}), ...style, ...(compact ? { fontSize: '12px' } : {}) }}>
        <thead>
          <tr>
            {headers.map((h, idx) => (
              <th
                key={idx}
                style={{
                  fontWeight: 500,
                  color: '#333',
                  fontSize: compact ? '0.85rem' : '1rem',
                  ...(colWidth ? { width: colWidth } : {}),
                  ...(compact ? { padding: '4px 6px' } : {}),
                }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={headers.length} style={{ color: '#999', ...denseCellStyle }}>
                Sin registros
              </td>
            </tr>
          )}
          {rows.map((r, idx) => (
            <tr key={idx}>
              {r.map((c, jdx) => (
                <td key={jdx} style={{ ...(colWidth ? { width: colWidth } : {}), ...denseCellStyle }}>
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const CalidadAnalisisVisualizacion = () => {
  const history = useHistory();
  const { uuid } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [identifier, setIdentifier] = useState(uuid || '');
  const reportRef = useRef(null);

  useEffect(() => {
    // Determina el identificador a usar: primero URL, luego localStorage
    if (uuid) {
      setIdentifier(uuid);
      return;
    }
    const cfg = getJSON(`${env.appName}-calidad`, {});
    const fallbackUuid = cfg?.uuid_calidad_analisis || cfg?.info_muestra?.uuid_calidad_analisis;
    const fallbackId = cfg?.id_calidad_analisis;
    setIdentifier(fallbackUuid || fallbackId || '');
  }, [uuid]);

  useEffect(() => {
    const fetchData = async () => {
      if (!identifier) {
        setLoading(false);
        return;
      }
      try {
        // Obtiene el análisis unificado por UUID o ID
        const resp = await Api.get('calidad/analisis', identifier);
        const body = resp?.data?.data || resp?.data || null;
        setData(body);
      } catch (e) {
        console.log(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [identifier]);

  const info = data?.info_muestra || {};
  const fisico = data?.analisis_fisico || {};
  const oro = data?.analisis_oro || {};
  const sensorial = data?.analisis_sensorial || {};

  // Etiqueta dinámica para mostrar el nombre según tipo de análisis (como en PDF)
  const tipoAnalisisLabel = (() => {
    const t = Number(info?.id_tipo_analisis ?? 0);
    switch (t) {
      case 1:
        return 'Productor';
      case 2:
        return 'Lote';
      case 3:
        return 'Región';
      case 4:
        return 'Otro Proveedor';
      default:
        return 'Nombre';
    }
  })();

  // Determina si existen datos sensoriales "significativos" para mostrar la sección
  const hasSensorial = (() => {
    const s = sensorial || {};
    const textFields = [
      'perfil_taza',
      'fragancia_aroma_desc',
      'sabor_desc',
      'sabor_residual_desc',
      'acidez_desc',
      'cuerpo_desc',
      'balance_desc',
      'uniformidad_desc',
      'taza_limpia_desc',
      'dulzor_desc',
      'catador_desc',
    ];
    const numericFields = [
      'fragancia_aroma',
      'sabor',
      'sabor_residual',
      'acidez',
      'cuerpo',
      'balance',
      'uniformidad',
      'taza_limpia',
      'dulzor',
      'catador',
      'defectos_no_tazas',
      'defectos_intensidad',
      'puntaje_calculado',
      'puntaje_manual',
    ];
    const hasText = textFields.some(k => {
      const v = s?.[k];
      return typeof v === 'string' && v.trim() !== '';
    });
    const hasNum = numericFields.some(k => {
      const v = s?.[k];
      return (typeof v === 'number' && v > 0) || (typeof v === 'string' && v.trim() !== '');
    });
    // Si el backend no creó registro vacío, tener algún id también indica existencia
    const hasId = !!(s?.id_calidad_analisis_sensorial || s?.uuid_calidad_analisis_sensorial);
    return hasText || hasNum || hasId;
  })();

  const zarandas = Array.isArray(oro?.zarandas) ? oro.zarandas : [];
  const defectosPrimarios = Array.isArray(oro?.defectos_primarios) ? oro.defectos_primarios : [];
  const defectosSecundarios = Array.isArray(oro?.defectos_secundarios) ? oro.defectos_secundarios : [];

  // Cálculos derivados para Análisis físico (resumen)
  const pmPerg = Number(fisico?.pergamino_peso_muestra_pergamino ?? 0);
  const poOro = Number(fisico?.pergamino_peso_muestra_oro ?? 0);
  const dfMancha = Number(fisico?.pergamino_peso_mancha ?? 0);
  const rendimientoBruto = calcRendimientoBruto(pmPerg, poOro);
  const manchaPorcentaje = calcManchaPorcentaje(pmPerg, dfMancha);
  // Valores provenientes de la API para el resumen de Análisis oro
  const pesoCafeLimpio = oro?.peso_cafe_limpio_grs ?? info?.peso_cafe_limpio_grs;
  const rendimientoExportable = oro?.rendimiento_exportable_por ?? info?.rendimiento_exportable_por;
  const perdidaProcesamientoPor = oro?.perdida_procesamiento_por ?? info?.perdida_procesamiento_por;
  const rendimientoReal =
    rendimientoExportable !== '' &&
    rendimientoExportable != null &&
    !isNaN(Number(rendimientoExportable)) &&
    perdidaProcesamientoPor !== '' &&
    perdidaProcesamientoPor != null &&
    !isNaN(Number(perdidaProcesamientoPor))
      ? Number(Number(rendimientoExportable) - Number(perdidaProcesamientoPor)).toFixed(2)
      : '';
  // Cálculo de Defectos (sensorial) centralizado en módulo de cálculos
  const defectosScore = calcDefectosScore(sensorial?.defectos_no_tazas, sensorial?.defectos_intensidad);
  // Puntaje en taza (sensorial) centralizado
  const puntajeTaza = calcPuntajeSensorial(sensorial || {});

  const hasOroZarandas =
    Array.isArray(zarandas) &&
    zarandas.some(z => {
      const idz = Number(z?.id_zaranda ?? 0);
      const val = String(z?.valor ?? '').trim();
      return idz > 0 || val !== '';
    });
  const hasOroDefPrimarios =
    Array.isArray(defectosPrimarios) &&
    defectosPrimarios.some(d => {
      const def = String(d?.defectos ?? '').trim();
      return def !== '';
    });
  const hasOroDefSecundarios =
    Array.isArray(defectosSecundarios) &&
    defectosSecundarios.some(d => {
      const def = String(d?.defectos ?? '').trim();
      return def !== '';
    });

  const hasOroCampos = [oro?.total_defectos_gramos, pesoCafeLimpio, rendimientoExportable, perdidaProcesamientoPor].some(
    v => v !== '' && v != null && !isNaN(Number(v)) && Number(v) > 0
  );
  const hasOro = hasOroZarandas || hasOroDefPrimarios || hasOroDefSecundarios || hasOroCampos;

  const zarandasRows = hasOroZarandas
    ? zarandas.filter(z => {
        const val = String(z?.valor ?? '').trim();
        return val !== '';
      })
    : [];
  const defectosPrimariosRows = hasOroDefPrimarios
    ? defectosPrimarios.filter(d => {
        const def = String(d?.defectos ?? '').trim();
        return def !== '';
      })
    : [];
  const defectosSecundariosRows = hasOroDefSecundarios
    ? defectosSecundarios.filter(d => {
        const def = String(d?.defectos ?? '').trim();
        return def !== '';
      })
    : [];

  const isValid = v => v !== '' && v != null && !isNaN(Number(v)) && Number(v) > 0;
  const isValidText = v => typeof v === 'string' && v.trim() !== '';
  const totalDefectosConteo = calcTotalDefectosRedondeado(defectosPrimariosRows, defectosSecundariosRows);
  return (
    <div>
      {/* Barra de acciones */}
      <div className="d-flex align-items-center mt-3 no-print" style={{ gap: 8 }}>
        <button className="btn btn-outline-dark" type="button" onClick={() => history.push({ pathname: '/admin/calidad' })}>
          <i className="fa fa-arrow-left"></i>
          {' Regresar al listado'}
        </button>
        <LinkDownload
          url={`calidad/analisis/recibo/descargar/${info?.uuid_calidad_analisis || ''}`}
          label={'PDF'}
          icon={env.images.icon.pdf}
        />
      </div>

      {/* Contenedor del reporte con ancho fijo ~A4 */}
      <div className="print-container" style={{ maxWidth: '750px', width: '100%', margin: '16px 0' }} ref={reportRef}>
        <CardContainer height={'100%'} style={{ marginTop: 0 }}>
          {loading && (
            <div className="row">
              <div className="col">Cargando...</div>
            </div>
          )}
          {!loading && !data && (
            <div className="row">
              <div className="col" style={{ color: '#b00' }}>
                {'No se encontró el análisis'}
                {identifier ? null : (
                  <div style={{ color: '#666', marginTop: 8 }}>
                    {'Sugerencia: guarda el análisis o abre esta vista desde el diálogo de confirmación para usar el UUID de localStorage.'}
                  </div>
                )}
              </div>
            </div>
          )}
          {!loading && data && (
            <>
              {/* Información general (como en el PDF) */}
              <div className="row mt-3">
                <div className="col-lg-12">
                  <h4>{'Información general de la muestra'}</h4>
                </div>
              </div>
              <div className="row mt-2">
                <div className="col-lg-6">
                  <div style={{ backgroundColor: '#eee', width: '90%', padding: 10, borderRadius: 8 }}>
                    <div>
                      Fecha: <span style={{ fontWeight: 'bold' }}>{info?.fecha ?? ''}</span>
                    </div>
                    {/* Usar id_muestra si se registró en el formulario, si no , usar folio que se genera automáticamente */}
                    <div>
                      Folio: <span style={{ fontWeight: 'bold' }}>{info?.id_muestra ?? info?.folio ?? ''}</span>
                    </div>
                    <div>
                      {tipoAnalisisLabel}: <span style={{ fontWeight: 'bold' }}>{String(info?.nombre ?? '')}</span>
                    </div>
                  </div>
                </div>
                <div className="col-lg-6">
                  <div style={{ backgroundColor: '#eee', width: '85%', padding: 10, borderRadius: 8 }}>
                    <div>
                      Tipo de Muestra: <span style={{ fontWeight: 'bold' }}>{String(info?.nombre_tipo_muestra ?? '')}</span>
                    </div>
                    <div>
                      Tipo Proceso: <span style={{ fontWeight: 'bold' }}>{String(info?.nombre_tipo_proceso ?? '')}</span>
                    </div>
                    <div>
                      Tipo de café: <span style={{ fontWeight: 'bold' }}>{String(info?.nombre_tipo_cafe ?? '')}</span>
                    </div>
                  </div>
                </div>
              </div>
              <Separator />

              {/* Análisis físico */}
              <div className="row mt-3">
                <div className="col-lg-12">
                  <h5>{'Análisis físico'}</h5>
                </div>
              </div>

              <div className="row mt-3">
                {isValid(fisico?.pergamino_peso_muestra_pergamino) && (
                  <div className="col-lg-4 col-xs-6">
                    <RowItem
                      label={'Peso muestra pergamino (grs.)'}
                      value={fisico?.pergamino_peso_muestra_pergamino}
                      size="sm"
                      stacked={true}
                    />
                  </div>
                )}

                {isValid(fisico?.pergamino_peso_muestra_oro) && (
                  <div className="col-lg-4 col-xs-6">
                    <RowItem label={'Peso muestra oro (grs.)'} value={fisico?.pergamino_peso_muestra_oro} size="sm" stacked={true} />
                  </div>
                )}

                {isValid(fisico?.pergamino_humedad) && (
                  <div className="col-lg-4 col-xs-6">
                    <RowItem label={'Humedad (%)'} value={fisico?.pergamino_humedad} size="sm" stacked={true} />
                  </div>
                )}

                {isValid(fisico?.olor_muestra) && (
                  <div className="col-lg-4 col-xs-6">
                    <RowItem label={'Olor / Aroma'} value={fisico?.olor_muestra} size="sm" stacked={true} />
                  </div>
                )}

                {isValid(fisico?.pergamino_densidad) && (
                  <div className="col-lg-4 col-xs-6">
                    <RowItem label={'Densidad (g./l.)'} value={fisico?.pergamino_densidad} size="sm" stacked={true} />
                  </div>
                )}

                {isValid(fisico?.olor_muestra) && (
                  <div className="col-lg-4 col-xs-6">
                    <RowItem label={'Olor / Aroma'} value={fisico?.olor_muestra} size="sm" stacked={true} />
                  </div>
                )}

                {isValid(fisico?.pergamino_peso_mancha) && (
                  <div className="col-lg-4 col-xs-6">
                    <RowItem label={'Defectos (gramos)'} value={fisico?.pergamino_peso_mancha} size="sm" stacked={true} />
                  </div>
                )}

                {isValidText(fisico?.nombre_color_muestra) && (
                  <div className="col-lg-4 col-xs-6">
                    <RowItem label={'Color de la muestra'} value={String(fisico?.nombre_color_muestra ?? '')} size="sm" stacked={true} />
                  </div>
                )}

                {isValid(fisico?.pergamino_actividad_agua) && (
                  <div className="col-lg-4 col-xs-6">
                    <RowItem label={'Actividad en agua'} value={fisico?.pergamino_actividad_agua} size="sm" stacked={true} />
                  </div>
                )}

                {isValid(rendimientoBruto) && (
                  <div className="col-lg-4 col-xs-6">
                    <RowItem label={'Rendimiento Bruto (%)'} value={rendimientoBruto} size="sm" stacked={true} />
                  </div>
                )}

                {isValid(manchaPorcentaje) && (
                  <div className="col-lg-4 col-xs-6">
                    <RowItem label={'Mancha/Defectos (%)'} value={manchaPorcentaje} size="sm" stacked={true} />
                  </div>
                )}
              </div>

              {hasOro && (
                <>
                  <Separator />
                  {/* Análisis oro */}
                  <div className="row mt-3">
                    <div className="col-lg-12">
                      <h5>{'Análisis oro'}</h5>
                    </div>
                  </div>

                  {/* Zarandas y defectos (50% de ancho) */}

                  <div className="row mt-3">
                    <div className="col-lg-3">
                      {isValid(oro?.pergamino_peso_muestra_oro_2) && (
                        <RowItem label={'Peso muestra oro (grs.)'} value={oro?.pergamino_peso_muestra_oro_2} size="sm" stacked={true} />
                      )}
                    </div>
                  </div>

                  <div className="row mt-3 oro-row-print">
                    <div className="col-lg-6">
                      {hasOroZarandas && zarandasRows.length > 0 && (
                        <>
                          <div className="row mt-2">
                            <div className="col-lg-12" style={{ color: '#666', fontWeight: 700, fontSize: '0.9rem' }}>
                              Zarandas
                            </div>
                          </div>
                          <ListTable
                            headers={['Nombre', 'Valor']}
                            rows={zarandasRows.map(z => [String(z?.nombre_zaranda ?? z?.id_zaranda ?? ''), String(z?.valor ?? '')])}
                            style={{ fontSize: '0.9rem' }}
                            className="print-half"
                            equalCols={true}
                            compact={true}
                          />
                        </>
                      )}

                      {hasOroDefPrimarios && defectosPrimariosRows.length > 0 && (
                        <>
                          <div className="row mt-2">
                            <div className="col-lg-12" style={{ color: '#666', fontWeight: 700, fontSize: '0.9rem' }}>
                              Defectos primarios
                            </div>
                          </div>
                          <ListTable
                            headers={['Tipo', 'Granos', 'Defectos']}
                            rows={defectosPrimariosRows.map(d => [
                              String(d?.nombre_mancha ?? d?.id_tipo_mancha ?? ''),
                              String(d?.no_granos ?? d?.granos ?? ''),
                              String(d?.defectos ?? ''),
                            ])}
                            style={{ fontSize: '0.9rem' }}
                            className="print-half"
                            equalCols={true}
                            compact={true}
                          />
                        </>
                      )}

                      {hasOroDefSecundarios && defectosSecundariosRows.length > 0 && (
                        <>
                          <div className="row mt-2">
                            <div className="col-lg-12" style={{ color: '#666', fontWeight: 700, fontSize: '0.9rem' }}>
                              Defectos secundarios
                            </div>
                          </div>
                          <ListTable
                            headers={['Tipo mancha', 'Granos', 'Defectos']}
                            rows={defectosSecundariosRows.map(d => [
                              String(d?.nombre_mancha ?? d?.id_tipo_mancha ?? ''),
                              String(d?.no_granos ?? d?.granos ?? ''),
                              String(d?.defectos ?? ''),
                            ])}
                            className="print-half"
                            equalCols={true}
                            compact={true}
                          />
                        </>
                      )}
                    </div>
                    <div className="col-lg-6">
                      <GraficaDefectos data={oro || {}} />
                    </div>
                  </div>

                  <div className="row mt-3">
                    {isValid(totalDefectosConteo) && (
                      <div className="col-lg-4">
                        <RowItem label={'No. Defectos'} value={totalDefectosConteo} size="sm" stacked={true} />
                      </div>
                    )}
                    {isValid(oro?.total_defectos_gramos) && (
                      <div className="col-lg-4">
                        <RowItem label={'Total de defectos (grs.)'} value={oro?.total_defectos_gramos} size="sm" stacked={true} />
                      </div>
                    )}
                    {isValid(pesoCafeLimpio) && (
                      <div className="col-lg-4">
                        <RowItem label={'Peso café limpio (grs.)'} value={pesoCafeLimpio} size="sm" stacked={true} />
                      </div>
                    )}
                  </div>
                  <div className="row mt-2">
                    {isValid(rendimientoExportable) && (
                      <div className="col-lg-4">
                        <RowItem label={'Rendimiento exportable (%)'} value={rendimientoExportable} size="sm" stacked={true} />
                      </div>
                    )}
                    {isValid(oro?.perdida_procesamiento_por) && (
                      <div className="col-lg-4">
                        <RowItem label={'Pérdida por procesamiento (%)'} value={oro?.perdida_procesamiento_por} size="sm" stacked={true} />
                      </div>
                    )}
                    {isValid(rendimientoReal) && (
                      <div className="col-lg-4">
                        <RowItem label={'Rendimiento real (%)'} value={rendimientoReal} size="sm" stacked={true} />
                      </div>
                    )}
                  </div>

                  <Separator />
                </>
              )}

              {/* Análisis sensorial */}
              {hasSensorial && (
                <>
                  <div className="row mt-3">
                    <div className="col-lg-12">
                      <h5>{'Análisis sensorial'}</h5>
                    </div>
                  </div>
                  <div className="row sensorial-row" style={{ display: 'flex', flexWrap: 'nowrap' }}>
                    <div className="col-md-6 col-xl-6">
                      <ListTable
                        headers={['Medición', 'Puntuación', 'Descripción']}
                        rows={[
                          ['Perfil de la taza', '-', String(sensorial?.perfil_taza ?? '')],
                          ['Fragancia / aroma', String(sensorial?.fragancia_aroma ?? ''), String(sensorial?.fragancia_aroma_desc ?? '')],
                          ['Sabor', String(sensorial?.sabor ?? ''), String(sensorial?.sabor_desc ?? '')],
                          ['Sabor residual', String(sensorial?.sabor_residual ?? ''), String(sensorial?.sabor_residual_desc ?? '')],
                          ['Acidez', String(sensorial?.acidez ?? ''), String(sensorial?.acidez_desc ?? '')],
                          ['Sensación en boca', String(sensorial?.cuerpo ?? ''), String(sensorial?.cuerpo_desc ?? '')],
                          ['Balance', String(sensorial?.balance ?? ''), String(sensorial?.balance_desc ?? '')],
                          ['Uniformidad', Number(sensorial?.uniformidad * 2) ?? '', String(sensorial?.uniformidad_desc ?? '')],
                          ['Taza limpia', Number(sensorial?.taza_limpia * 2) ?? '', String(sensorial?.taza_limpia_desc ?? '')],
                          ['Dulzor', Number(sensorial?.dulzor * 2) ?? '', String(sensorial?.dulzor_desc ?? '')],
                          ['Defectos', defectosScore !== '' ? defectosScore : '', ''],
                          ['Catador', String(sensorial?.catador ?? ''), String(sensorial?.catador_desc ?? '')],
                          ['Puntaje en Taza', String(sensorial?.puntaje_manual ?? ''), ''],
                        ]}
                        className="print-half"
                        style={{ fontSize: '12px' }}
                        compact={true}
                      />

                      {/* Sección "Descripción / Notas adicionales" eliminada */}
                    </div>
                    <div className="col-md-6 col-xl-6">
                      <GraficaSensorial data={sensorial || {}} width={340} height={340} />
                    </div>
                  </div>
                  <div className="mt-2" style={{ marginLeft: '0px' }}>
                    <div style={{ color: '#666', fontWeight: 700, fontSize: '0.9rem' }}>Observaciones</div>
                    <p style={{ fontSize: '0.9rem', marginTop: '5px' }}>{String(info?.observaciones ?? '')}</p>
                  </div>
                </>
              )}
            </>
          )}
        </CardContainer>
      </div>
    </div>
  );
};

export default CalidadAnalisisVisualizacion;
